// Sentry bootstrap. Reads VITE_SENTRY_DSN from the build-time env. If the
// variable is missing or blank, every export here becomes a no-op so the app
// keeps working in dev / preview without a DSN configured.
//
// What we capture:
//  - Unhandled exceptions and unhandled promise rejections (default)
//  - Breadcrumbs from console + fetch + xhr (default Sentry browser
//    integrations) -- enough context to debug a user-reported error
//  - Release tagged with VITE_SENTRY_RELEASE so source-map symbolication
//    can land later without a code change

import * as Sentry from "@sentry/react";

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENV = (import.meta.env.VITE_SENTRY_ENV as string | undefined)
  ?? import.meta.env.MODE
  ?? "production";
const RELEASE = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;

let initialized = false;

export function initSentry(): void {
  if (initialized || !DSN) return;
  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    // Conservative sample rates -- we want errors first; perf data can be
    // turned up via env once the bill is known.
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_RATE ?? 0.0),
    replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAY_ERROR_RATE ?? 0.0),
    replaysSessionSampleRate: 0,
    sendDefaultPii: false,
  });
  initialized = true;
}

export function captureException(err: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return;
  Sentry.captureException(err, context ? { extra: context } : undefined);
}

export function setUser(user: { id?: string; email?: string } | null): void {
  if (!initialized) return;
  Sentry.setUser(user ? { id: user.id, email: user.email } : null);
}

// Re-export the React error boundary HOC so callers don't need to know
// whether the wiring is on or off.
export const SentryErrorBoundary = Sentry.ErrorBoundary;
